import { SearchSelect } from '@wundo/ui';
import { searchOrganisations, type Organisation } from '@/lib/organisations.ts';

interface OrganisationPickerBaseProps {
  /** Put on the field, so a `<label htmlFor>` names it. */
  id?: string;
  /** Names the field where no visible label does. */
  'aria-label'?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Offers an × that empties the field. */
  clearable?: boolean;
  className?: string;
}

export interface OrganisationPickerSingleProps extends OrganisationPickerBaseProps {
  multiple?: false;
  value: Organisation | null;
  onChange: (value: Organisation | null) => void;
}

export interface OrganisationPickerMultipleProps extends OrganisationPickerBaseProps {
  multiple: true;
  value: Organisation[];
  onChange: (value: Organisation[]) => void;
}

export type OrganisationPickerProps =
  | OrganisationPickerSingleProps
  | OrganisationPickerMultipleProps;

/**
 * Pick an organisation by typing its name.
 *
 * The console's one organisation field — a `SearchSelect` over the search
 * endpoint, so the whole list never has to be loaded to choose one from it.
 * It works in whole records rather than ids: what a caller gets back is the
 * organisation, and the id is one property of it.
 */
export function OrganisationPicker(props: OrganisationPickerProps) {
  const shared = {
    loadOptions: (query: string, signal: AbortSignal) =>
      searchOrganisations(query, signal),
    getKey: (organisation: Organisation) => organisation.id,
    getLabel: (organisation: Organisation) => organisation.name,
    placeholder: props.placeholder ?? 'Search organisations…',
    id: props.id,
    'aria-label': props['aria-label'],
    disabled: props.disabled,
    invalid: props.invalid,
    clearable: props.clearable,
    className: props.className,
  };

  if (props.multiple === true) {
    return (
      <SearchSelect {...shared} multiple value={props.value} onChange={props.onChange} />
    );
  }

  return <SearchSelect {...shared} value={props.value} onChange={props.onChange} />;
}
